import { NurseLayout } from "@/components/layout/nurse-layout";
import { useListMedications } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight, FlaskConical } from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Highlight } from "@/components/ui/highlight";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DRUG_CLASS_GROUPS = [
  { value: "", label: "All" },
  { value: "cDMARD", label: "cDMARDs" },
  { value: "bDMARD", label: "bDMARDs" },
  { value: "tsDMARD", label: "tsDMARDs" },
  { value: "NSAID", label: "NSAIDs" },
  { value: "Steroid", label: "Steroids" },
  { value: "Other", label: "Other" },
];

const classColors: Record<string, string> = {
  cdmard: "bg-blue-50 text-blue-700 border-blue-200",
  bdmard: "bg-purple-50 text-purple-700 border-purple-200",
  tsdmard: "bg-indigo-50 text-indigo-700 border-indigo-200",
  nsaid: "bg-orange-50 text-orange-700 border-orange-200",
  steroid: "bg-amber-50 text-amber-700 border-amber-200",
  other: "bg-slate-50 text-slate-700 border-slate-200",
};

const classHeaderColors: Record<string, string> = {
  cdmard: "border-blue-200 bg-blue-50",
  bdmard: "border-purple-200 bg-purple-50",
  tsdmard: "border-indigo-200 bg-indigo-50",
  nsaid: "border-orange-200 bg-orange-50",
  steroid: "border-amber-200 bg-amber-50",
  other: "border-slate-200 bg-slate-50",
};

const classTextColors: Record<string, string> = {
  cdmard: "text-blue-800",
  bdmard: "text-purple-800",
  tsdmard: "text-indigo-800",
  nsaid: "text-orange-800",
  steroid: "text-amber-800",
  other: "text-slate-700",
};

function classKey(drugClass: string): string {
  const k = drugClass.toLowerCase().replace(/[^a-z]/g, "");
  if (k.includes("bdmard") || k.includes("biologic")) return "bdmard";
  if (k.includes("cdmard") || k.includes("conventional")) return "cdmard";
  if (k.includes("tsdmard") || k.includes("jak") || k.includes("targeted")) return "tsdmard";
  if (k.includes("nsaid")) return "nsaid";
  if (k.includes("steroid") || k.includes("cortico") || k.includes("glucocortico")) return "steroid";
  return "other";
}

function getDrugClassColor(drugClass: string) {
  return classColors[classKey(drugClass)] ?? classColors.other;
}

function groupKeyForFilter(filterValue: string): string {
  return classKey(filterValue);
}

const SORT_OPTIONS = [
  { value: "az", label: "A–Z" },
  { value: "za", label: "Z–A" },
  { value: "recent", label: "Recently added" },
];

export default function NursePharmacology() {
  const [search, setSearch] = useSearchParam("q", "");
  const [activeClass, setActiveClass] = useSearchParam("class", "");
  const [sort, setSort] = useSearchParam("sort", "az");

  const { data: medications, isLoading } = useListMedications({ search: search || undefined });

  const filtered = useMemo(() => {
    const base = medications?.filter(m => {
      if (!activeClass) return true;
      return classKey(m.drugClass ?? "") === groupKeyForFilter(activeClass);
    });
    if (!base) return base;
    if (sort === "za") return [...base].sort((a, b) => b.name.localeCompare(a.name));
    if (sort === "recent") return [...base].sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [medications, activeClass, sort]);

  const grouped = useMemo(() => {
    if (!filtered) return [];
    const groupsToShow = activeClass
      ? DRUG_CLASS_GROUPS.filter(g => g.value === activeClass)
      : DRUG_CLASS_GROUPS.filter(g => g.value !== "");
    return groupsToShow
      .map(g => ({
        ...g,
        key: groupKeyForFilter(g.value || "other"),
        items: filtered.filter(m => g.value === "" || classKey(m.drugClass ?? "") === groupKeyForFilter(g.value)),
      }))
      .filter(g => g.items.length > 0);
  }, [filtered, activeClass]);

  const resultCount = filtered?.length ?? 0;

  if (isLoading) return <NurseLayout title="Pharmacology Reference"><LoadingScreen /></NurseLayout>;

  return (
    <NurseLayout title="Pharmacology Reference">
      <div className="mb-6">
        <p className="text-slate-600 mb-6">
          Complete pharmacology reference — PK/PD, dosage protocols, adverse effects, and live drug interaction checker.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search drugs…"
              className="pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            {DRUG_CLASS_GROUPS.map(g => {
              const count = g.value === ""
                ? (medications?.length ?? 0)
                : (medications?.filter(m => classKey(m.drugClass ?? "") === groupKeyForFilter(g.value)).length ?? 0);
              return (
                <button
                  key={g.value}
                  onClick={() => setActiveClass(g.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                    activeClass === g.value
                      ? "bg-teal-700 text-white border-teal-700 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
                  }`}
                >
                  {g.label}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none ${
                    activeClass === g.value ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <p className={`mt-3 text-sm ${resultCount === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {resultCount === 0 ? "No drugs found" : `Showing ${resultCount} ${resultCount === 1 ? "drug" : "drugs"}`}
      </p>

      {grouped.length === 0 && (
        <div className="py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          No drugs found matching your filters.
        </div>
      )}

      <div className="space-y-10">
        {grouped.map(group => (
          <section key={group.value || "all"}>
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border mb-4 ${classHeaderColors[group.key] ?? classHeaderColors.other}`}>
              <h2 className={`text-sm font-bold uppercase tracking-wider ${classTextColors[group.key] ?? classTextColors.other}`}>
                {group.label}
              </h2>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70 border ${classColors[group.key] ?? classColors.other}`}>
                {group.items.length} {group.items.length === 1 ? "drug" : "drugs"}
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {group.items.map(m => (
                <Link key={m.id} href={`/nurse/pharmacology/${m.id}`}>
                  <Card className="h-full hover:border-teal-500 hover:shadow-md transition-all cursor-pointer group">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${getDrugClassColor(m.drugClass ?? "")}`}>
                          {m.drugClass}
                        </span>
                        {m.pharmacokinetics && (
                          <FlaskConical className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-label="Full pharmacology data available" />
                        )}
                      </div>
                      <CardTitle className="text-xl group-hover:text-teal-700 transition-colors"><Highlight text={m.name} query={search} className="text-teal-700" /></CardTitle>
                      {m.genericName && <p className="text-xs text-slate-500 italic"><Highlight text={m.genericName} query={search} className="text-teal-700" /></p>}
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm mb-3">
                        <span className="font-semibold text-slate-700">Indication:</span>{" "}
                        <span className="text-slate-600"><Highlight text={m.indication ?? ""} query={search} className="text-teal-700" /></span>
                      </div>
                      {m.drugClassifications && m.drugClassifications.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {m.drugClassifications.slice(0, 2).map(dc => (
                            <Badge key={dc} variant="outline" className="text-xs text-slate-500">{dc}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-teal-600 font-medium">
                        <span>PK/PD, dosage & interactions</span>
                        <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </NurseLayout>
  );
}
